import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { user_header } from "./Sidebar_path";
import { Search, User, LogOut, Download } from "lucide-react";
import * as Icons from "lucide-react";
import { getUserFromToken } from "../../Utils/TokenVerify";
import { getCompanyApi } from "../../Services/Superadmin/Superadmin";
import { useLivePrices } from "../../context/LivePriceContext";
import ApiService from "../../Services/user/service";
import { LogoutUser } from "../../Services/Admin/Addmin";
import Swal from "sweetalert2";
import socket from "../../Utils/socketClient";

const UserHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const TokenData = getUserFromToken();
  const user_id = TokenData?.user_id;
  const username = localStorage.getItem("UserName") || "User";

  const [logo, setLogo] = useState("");
  const { livePrices } = useLivePrices();
  const [indexSymbols, setIndexSymbols] = useState([]);
  const [funds, setFunds] = useState([]);
  const currency = localStorage.getItem("currentCurrency") || "1";

  const getIconComponent = (name) => {
    switch (name.toLowerCase()) {
      case "dashboard": return Icons.LayoutDashboard;
      case "orders": return Icons.ListChecks;
      case "portfolio": return Icons.Briefcase;
      case "option chain": return Icons.LineChart;
      case "news": return Icons.Newspaper;
      case "research": return Icons.Zap;
      case "trade statement": return Icons.FileText;
      case "ledger report": return Icons.Book;
      case "balance statement": return Icons.Wallet;
      case "registered bank": return Icons.Building;
      case "kyc document":
      case "kyc": return Icons.CreditCard;
      case "privacy policy": return Icons.Shield;
      case "terms & conditions": return Icons.Scale;
      case "refer & earn": return Icons.Share2;
      case "payment history": return Icons.Clock;
      case "my favorites": return Icons.Star;
      default: return Icons.Circle;
    }
  };

  useEffect(() => {
    const fetchLogo = async () => {
      const res = await getCompanyApi();
      if (res && res.data) {
        setLogo(res.data.logo);
      }
    };
    fetchLogo();
  }, []);

  const fetchIndexSymbols = async () => {
    try {
      const res = await ApiService.indexSymbols();
      if (res.data && res.data.status) {
        setIndexSymbols(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching index symbols:", err);
    }
  };

  useEffect(() => {
    if (currency === "1") {
      fetchIndexSymbols();
    }
  }, [currency]);

  useEffect(() => {
    let subscribedChannels = null;
    if (currency === "1" && indexSymbols.length > 0) {
      const subscribe = () => {
        const channels = indexSymbols
          .filter(item => item.exch_seg && item.instrument_token)
          .map(item => `${item.exch_seg}|${item.instrument_token}`)
          .join("#");
        if (channels) {
          socket.emit("subscribe", channels);
          subscribedChannels = channels;
        }
      };

      if (socket.connected) {
        subscribe();
      }
      socket.on("connect", subscribe);
      return () => {
        socket.off("connect", subscribe);
        if (subscribedChannels) {
          socket.emit("unsubscribe", subscribedChannels);
        }
      };
    }
  }, [indexSymbols, currency]);

  useEffect(() => {
    const getUserDetail = async () => {
      if (user_id) {
        const res = await ApiService.getUserDetail(user_id);
        setFunds(res?.data?.data || []);
      }
    };
    getUserDetail();
  }, [user_id]);

  const getIndexLivePrice = (symbolName, fallbackVal) => {
    if (currency !== "1") {
      return livePrices[symbolName] || livePrices[symbolName.toLowerCase()] || fallbackVal;
    }
    const symbolObj = indexSymbols.find(
      (item) => item.symbol?.toUpperCase() === symbolName.toUpperCase()
    );
    if (!symbolObj) return fallbackVal;
    const token = symbolObj.instrument_token;
    const price = livePrices[token] || symbolObj.lp || symbolObj.liveprice;
    return price || fallbackVal;
  };

  const logoutuser = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, logout",
    });

    if (result.isConfirmed) {
      const response = await LogoutUser({ userid: user_id });
      if (response.status) {
        localStorage.clear();
        navigate("/login");
      }
    }
  };

  const fundBalance = funds.length > 0 ? (currency === "1" ? (funds[0].Inr_Balance || 0).toFixed(2) : (funds[0].Balance || 0).toFixed(2)) : "0.00";

  const storedMarket = JSON.parse(localStorage.getItem("market")) || [0];
  const isToggleMode = storedMarket.length === 2;
  let market = 0;
  if (!isToggleMode) {
    market = storedMarket[0];
  } else {
    market = Number(localStorage.getItem("currentCurrency")) || 0;
  }

  const toggleCurrency = () => {
    const stored = JSON.parse(localStorage.getItem("market")) || [0];
    if (stored.length === 2) {
      const current = Number(localStorage.getItem("currentCurrency")) || 0;
      const next = current === 0 ? 1 : 0;
      localStorage.setItem("currentCurrency", next);
      window.location.reload();
      return;
    }
    localStorage.setItem("market", JSON.stringify([0, 1]));
    localStorage.setItem("currentCurrency", 0);
    window.location.reload();
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1050, fontFamily: "Inter, sans-serif" }}>
      {/* Top Nav: Dark Theme */}
      <div className="d-flex align-items-center justify-content-between px-4 py-2" style={{ backgroundColor: "#3736af", color: "#fff", height: "70px" }}>

        {/* Left: Logo and Tickers */}
        <div className="d-flex align-items-center gap-5 flex-grow-1" style={{ minWidth: 0 }}>
          <img src={logo} alt="Logo" style={{ maxHeight: "90px", objectFit: "contain", flexShrink: 0 }} />

          {/* Ticker Items */}
          <div className="d-none d-xl-flex align-items-center gap-5 overflow-auto" style={{ whiteSpace: "nowrap" }}>
            {currency === "1" ? (
              <>
                <div className="d-flex align-items-center gap-4">
                  <div className="d-flex align-items-center gap-1">
                    <span style={{ fontSize: "12px", color: "#aeb2c5", fontWeight: "600" }}>
                      NIFTY:
                    </span>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>
                      ₹{getIndexLivePrice("NIFTY", "0.00")}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-1">
                    <span style={{ fontSize: "12px", color: "#aeb2c5", fontWeight: "600" }}>
                      SENSEX:
                    </span>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>
                      ₹{getIndexLivePrice("SENSEX", "0.00")}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-1">
                    <span style={{ fontSize: "12px", color: "#aeb2c5", fontWeight: "600" }}>
                      BANKNIFTY:
                    </span>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>
                      ₹{getIndexLivePrice("BANKNIFTY", "0.00")}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="d-flex flex-column">
                  <span style={{ fontSize: "10px", color: "#aeb2c5", fontWeight: "600", textTransform: "uppercase" }}>BITCOIN</span>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>${getIndexLivePrice("BTCUSD", "0.00")}</span>
                  </div>
                </div>
                <div className="d-flex flex-column">
                  <span style={{ fontSize: "10px", color: "#aeb2c5", fontWeight: "600", textTransform: "uppercase" }}>ETHEREUM</span>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>${getIndexLivePrice("ETHUSD", "0.00")}</span>
                  </div>
                </div>
                <div className="d-flex flex-column">
                  <span style={{ fontSize: "10px", color: "#aeb2c5", fontWeight: "600", textTransform: "uppercase" }}>SOLANA</span>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>${getIndexLivePrice("SOLUSD", "0.00")}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Search & Profile & Toggle */}
        <div className="d-flex align-items-center gap-4">

          {isToggleMode && (
            <button
              onClick={toggleCurrency}
              className="btn btn-outline-dark d-flex align-items-center gap-2 px-3 py-1"
              style={{ borderRadius: "25px", border: "1px solid rgba(255,255,255,0.2)" }}
            >
              <span style={{ fontWeight: 600, fontSize: "12px", color: "#fff" }}>
                {market === 0 ? "USD" : "INR"}
              </span>
              <div
                style={{
                  width: 36,
                  height: 18,
                  borderRadius: 20,
                  background: market === 0 ? "#007bff" : "#28a745",
                  position: "relative",
                  transition: "0.3s",
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    background: "#fff",
                    borderRadius: "50%",
                    position: "absolute",
                    top: 2,
                    left: market === 0 ? 20 : 2,
                    transition: "0.3s",
                  }}
                />
              </div>
            </button>
          )}

          {/* Mobile Profile Direct Link */}
          <div className="d-md-none d-flex align-items-center gap-2" role="button" onClick={() => navigate("/user/profile")} style={{ cursor: "pointer" }}>
            <img src="/assets/images/avatar/1.png" alt="User" className="rounded-circle" style={{ width: "35px", height: "35px" }} />
          </div>

          {/* Desktop Profile Dropdown */}
          <div className="dropdown d-none d-md-block">
            <div className="d-flex align-items-center gap-2" role="button" data-bs-toggle="dropdown" aria-expanded="false" style={{ cursor: "pointer" }}>
              <img src="/assets/images/avatar/1.png" alt="User" className="rounded-circle" style={{ width: "35px", height: "35px" }} />
              <div className="text-end">
                <div style={{ fontSize: "13px", fontWeight: "600" }}>{username}</div>
                <div className="badge bg-success" style={{ fontSize: "10px" }}>Premium</div>
              </div>
            </div>
            <ul className="dropdown-menu dropdown-menu-end shadow">
              <li><Link className="dropdown-item text-dark" to="/user/profile"><User size={16} className="me-2" /> Profile</Link></li>
              <li><Link className="dropdown-item text-dark" to="/user/favorites"><Icons.Star size={16} className="me-2" /> My Favorites</Link></li>
              <li><Link className="dropdown-item text-dark" to="/user/payment-history"><Icons.History size={16} className="me-2" /> Payment History</Link></li>
              <li><Link className="dropdown-item text-dark" to="/user/privacy-policy"><Icons.Shield size={16} className="me-2" /> Privacy Policy</Link></li>
              <li><Link className="dropdown-item text-dark" to="/user/terms"><Icons.FileText size={16} className="me-2" /> Terms & Conditions</Link></li>
              <li><Link className="dropdown-item text-dark" to="/user/refer"><Icons.Share2 size={16} className="me-2" /> Refer & Earn</Link></li>
              <li><hr className="dropdown-divider" /></li>
              <li><button className="dropdown-item text-danger" onClick={logoutuser}><LogOut size={16} className="me-2" /> Logout</button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Nav: Light Theme (Tabs) */}
      <div className="bg-white shadow-sm border-bottom px-4 d-none d-md-flex align-items-center" style={{ height: "60px", minWidth: 0 }}>

        {/* Menu Items */}
        <div className="d-flex align-items-center gap-4 overflow-auto w-100 h-100" style={{ whiteSpace: "nowrap", overflowY: "hidden" }}>
          {user_header.map((item) => {
            const isActive = location.pathname === item.route;
            return (
              <Link
                key={item.id}
                to={item.route}
                className={`text-decoration-none d-flex align-items-center`}
                style={{
                  color: isActive ? "#1e204c" : "#666",
                  fontWeight: isActive ? "700" : "600",
                  fontSize: "13px",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  backgroundColor: isActive ? "rgba(30, 32, 76, 0.1)" : "transparent",
                  transition: "all 0.2s"
                }}
              >
                {item.name}
              </Link>
            );
          })}
        </div>



      </div>
    </div>
  );
};

export default UserHeader;
